import type {
  AnalyticsReport,
  AnalyticsReportFilters,
} from "@server/types/analyticsReportTypes";
import { analyticsReportSchema } from "@server/types/analyticsReportTypes";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useRequiredOrgId } from "@/hooks/useRequiredOrgId";
import { queryKeys } from "@/lib/query-keys";
import { apiGet, createOrgApiUrl } from "@/utils/apiUtils";

/** Cache identity includes the organization and every filter; no data carries across tenants. */
export type AnalyticsReportParams = AnalyticsReportFilters & {
  from: string;
  to: string;
};

/** Fetches one report for the selected window without polling; the dashboard controls rolling versus fixed-range refresh. */
export function useAnalyticsReport(params: AnalyticsReportParams) {
  const orgId = useRequiredOrgId();
  const previousOptions = useRef<
    { orgId: string; options: AnalyticsReport["options"] } | undefined
  >(undefined);
  const query = useQuery({
    queryKey: queryKeys.analytics.report(orgId, params),
    queryFn: async ({ signal }) => {
      const search = new URLSearchParams();
      for (const [key, value] of Object.entries(params))
        if (value !== undefined) search.set(key, String(value));
      const response = await apiGet<{ data: unknown }>(
        `${createOrgApiUrl(orgId, "analytics", "report")}?${search}`,
        { signal, timeout: 20_000 },
      );
      return analyticsReportSchema.parse(response.data);
    },
    staleTime: 60_000,
    gcTime: 30 * 60_000,
  });
  useEffect(() => {
    if (query.data)
      previousOptions.current = { orgId, options: query.data.options };
  }, [orgId, query.data]);
  return {
    ...query,
    options:
      query.data?.options ??
      (previousOptions.current?.orgId === orgId
        ? previousOptions.current.options
        : undefined),
  };
}
